"use server";

import { revalidatePath } from "next/cache";
import { z } from "zod";
import { prisma } from "@/lib/prisma";
import { requireUser } from "@/lib/session";
import { hostnameFromUrl, normalizeUrl, parseJson } from "@/lib/utils";
import { verificationToken } from "@/lib/crypto";
import { verifySite } from "@/lib/verify";
import { enqueueDetection, enqueueSubmission } from "@/lib/queue";
import { generateReportDraft, validateReport } from "@/lib/reports";
import { logActivity, notify } from "@/lib/activity";
import { lookupProvider } from "@/lib/providers";
import type { CaseStatus, ReportType, TargetPriority } from "@/lib/types";

function headersIp() {
  return undefined;
}

export async function createProtectedSite(formData: FormData) {
  const user = await requireUser();
  const schema = z.object({
    name: z.string().min(2),
    domain: z.string().min(3),
    ownerName: z.string().min(2),
    copyrightOwner: z.string().min(2),
    contactEmail: z.string().min(5),
    country: z.string().min(2),
    description: z.string().min(8),
    protectedAssets: z.string().optional(),
    method: z.enum(["DNS_TXT", "HTML_META"]),
  });
  const parsed = schema.parse({
    name: formData.get("name"),
    domain: hostnameFromUrl(String(formData.get("domain") ?? "")),
    ownerName: formData.get("ownerName"),
    copyrightOwner: formData.get("copyrightOwner"),
    contactEmail: formData.get("contactEmail"),
    country: formData.get("country"),
    description: formData.get("description"),
    protectedAssets: formData.get("protectedAssets"),
    method: formData.get("method") ?? "DNS_TXT",
  });
  const { method, protectedAssets, ...siteData } = parsed;

  const site = await prisma.protectedSite.create({
    data: {
      userId: user.id,
      ...siteData,
      protectedAssets: JSON.stringify(
        (protectedAssets ?? "")
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
      ),
      status: "UNVERIFIED",
      monitoringEnabled: false,
    },
  });

  await prisma.domainVerification.create({
    data: {
      siteId: site.id,
      method,
      token: verificationToken(),
    },
  });

  await logActivity({
    userId: user.id,
    userName: "workspace",
    action: "Registered protected website",
    target: site.domain,
    result: "SUCCESS",
    ip: headersIp(),
  });

  revalidatePath("/protected-sites");
  return { id: site.id };
}

export async function runVerification(siteId: string) {
  const user = await requireUser();
  const site = await prisma.protectedSite.findFirst({ where: { id: siteId, userId: user.id } });
  if (!site) throw new Error("Site not found");
  const result = await verifySite(siteId);
  await logActivity({
    userId: user.id,
    userName: "workspace",
    action: "Checked domain verification",
    target: site.domain,
    result: result.ok ? "SUCCESS" : "FAILED",
  });
  revalidatePath("/protected-sites");
  revalidatePath(`/protected-sites/${siteId}`);
  return result;
}

export async function confirmOwnership(siteId: string) {
  const user = await requireUser();
  const site = await prisma.protectedSite.findFirst({ where: { id: siteId, userId: user.id } });
  if (!site) throw new Error("Site not found");
  await prisma.protectedSite.update({
    where: { id: siteId },
    data: { status: site.monitoringEnabled ? "MONITORING" : "VERIFIED" },
  });
  await prisma.domainVerification.updateMany({
    where: { siteId },
    data: { status: "VERIFIED", verifiedAt: new Date(), error: null },
  });
  await logActivity({
    userId: user.id,
    userName: "workspace",
    action: "Checked domain verification",
    target: site.domain,
    result: "SUCCESS",
  });
  revalidatePath("/protected-sites");
  revalidatePath(`/protected-sites/${siteId}`);
}

export async function toggleMonitoring(siteId: string, enabled: boolean) {
  const user = await requireUser();
  const site = await prisma.protectedSite.findFirst({ where: { id: siteId, userId: user.id } });
  if (!site) throw new Error("Site not found");
  if (enabled && site.status === "UNVERIFIED") {
    throw new Error("Confirm you own this site first");
  }
  await prisma.protectedSite.update({
    where: { id: siteId },
    data: {
      monitoringEnabled: enabled,
      status: enabled ? "MONITORING" : site.status === "MONITORING" ? "VERIFIED" : site.status,
    },
  });
  revalidatePath("/protected-sites");
}

export async function createTarget(formData: FormData) {
  const user = await requireUser();
  const domain = hostnameFromUrl(String(formData.get("domain") ?? ""));
  const url = normalizeUrl(String(formData.get("url") ?? domain));
  const siteId = String(formData.get("siteId") ?? "") || null;
  if (siteId) {
    const site = await prisma.protectedSite.findFirst({ where: { id: siteId, userId: user.id } });
    if (!site) throw new Error("Protected site not found");
    if (site.status === "UNVERIFIED") {
      throw new Error("Confirm your site first, then you can link a suspect");
    }
  }
  const target = await prisma.target.create({
    data: {
      userId: user.id,
      siteId,
      domain,
      url,
      notes: String(formData.get("notes") ?? "") || null,
      priority: (String(formData.get("priority") ?? "MEDIUM") as TargetPriority) || "MEDIUM",
      tags: JSON.stringify(
        String(formData.get("tags") ?? "")
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
      ),
    },
  });
  await logActivity({
    userId: user.id,
    userName: "workspace",
    action: "Added suspected target",
    target: domain,
    result: "SUCCESS",
  });
  void refreshProviderIntel(target.id).catch(() => undefined);
  revalidatePath("/targets");
  return { id: target.id };
}

export async function refreshProviderIntel(targetId: string) {
  const user = await requireUser();
  const target = await prisma.target.findFirst({ where: { id: targetId, userId: user.id } });
  if (!target) throw new Error("Target not found");
  const intel = await lookupProvider(target.domain);
  await prisma.target.update({
    where: { id: targetId },
    data: {
      ip: intel.ip,
      asn: intel.asn,
      hostingProvider: intel.hostingProvider,
      cdn: intel.cdn ?? undefined,
      registrar: intel.registrar,
      country: intel.country,
      nameservers: JSON.stringify(intel.nameservers),
      lastSeen: new Date(),
    },
  });
  if (intel.hostingProvider) {
    await prisma.provider.upsert({
      where: { name_type: { name: intel.hostingProvider, type: "HOSTING" } },
      create: { name: intel.hostingProvider, type: "HOSTING", country: intel.country, officialChannel: "email" },
      update: { country: intel.country },
    });
  }
  revalidatePath(`/targets/${targetId}`);
}

export async function startDetection(formData: FormData) {
  const user = await requireUser();
  const siteId = String(formData.get("siteId") ?? "");
  const targetId = String(formData.get("targetId") ?? "");
  const site = await prisma.protectedSite.findFirst({ where: { id: siteId, userId: user.id } });
  const target = await prisma.target.findFirst({ where: { id: targetId, userId: user.id } });
  if (!site || !target) throw new Error("Site or target not found");
  if (site.status === "UNVERIFIED") throw new Error("Confirm your site first");

  const detection = await prisma.detection.create({
    data: { userId: user.id, siteId, targetId, status: "QUEUED" },
  });
  await enqueueDetection(detection.id);
  await logActivity({
    userId: user.id,
    userName: "workspace",
    action: "Queued detection crawl",
    target: target.domain,
    result: "SUCCESS",
  });
  revalidatePath("/detections");
  return { id: detection.id };
}

export async function updateCaseStatus(caseId: string, status: CaseStatus, notes?: string) {
  const user = await requireUser();
  const kase = await prisma.case.findFirst({ where: { id: caseId, userId: user.id } });
  if (!kase) throw new Error("Case not found");
  await prisma.case.update({
    where: { id: caseId },
    data: { status, notes: notes ?? kase.notes, updatedAt: new Date() },
  });
  if (status === "UNDER_REVIEW") {
    await notify({
      userId: user.id,
      title: "Case requires review",
      body: `${kase.caseCode} was moved to under review.`,
      type: "review",
      href: `/cases/${caseId}`,
    });
  }
  await logActivity({
    userId: user.id,
    userName: "workspace",
    action: `Updated case status to ${status}`,
    caseId: kase.caseCode,
    result: "SUCCESS",
  });
  revalidatePath("/cases");
  revalidatePath(`/cases/${caseId}`);
}

export async function createDraftReport(formData: FormData) {
  const user = await requireUser();
  const caseId = String(formData.get("caseId") ?? "");
  const reportType = String(formData.get("reportType") ?? "DMCA_COPYRIGHT_NOTICE") as ReportType;
  const result = await generateReportDraft({ userId: user.id, caseId, reportType });
  await logActivity({
    userId: user.id,
    userName: "workspace",
    action: result.duplicate ? "Blocked duplicate report draft" : "Generated report draft",
    caseId: caseId,
    result: result.duplicate ? "DUPLICATE REPORT" : "SUCCESS",
  });
  if (!result.duplicate) {
    await notify({
      userId: user.id,
      title: "Report draft generated",
      body: "A draft is ready for human review. It has not been submitted.",
      type: "report",
      href: `/reports/${result.report.id}`,
    });
  }
  revalidatePath("/reports");
  return result;
}

export async function validateDraft(reportId: string) {
  const user = await requireUser();
  const report = await prisma.report.findFirst({ where: { id: reportId, userId: user.id } });
  if (!report) throw new Error("Report not found");
  const result = await validateReport(reportId);
  if (result.status === "READY") {
    await prisma.case.update({ where: { id: report.caseId }, data: { status: "READY" } });
    await notify({
      userId: user.id,
      title: "Report ready",
      body: "Validation passed. Confirm ownership and accuracy before submission.",
      type: "report",
      href: `/reports/${reportId}`,
    });
  }
  revalidatePath(`/reports/${reportId}`);
  return result;
}

export async function approveAndSubmit(formData: FormData) {
  const user = await requireUser();
  const reportId = String(formData.get("reportId") ?? "");
  const ownershipChecked = formData.get("ownershipChecked") === "on";
  const evidenceChecked = formData.get("evidenceChecked") === "on";
  const accuracyChecked = formData.get("accuracyChecked") === "on";
  const legalChecked = formData.get("legalChecked") === "on";
  if (!ownershipChecked || !evidenceChecked || !accuracyChecked || !legalChecked) {
    throw new Error("All legal confirmations are required");
  }
  await prisma.report.update({
    where: { id: reportId },
    data: {
      ownershipChecked,
      evidenceChecked,
      accuracyChecked,
      legalChecked,
      goodFaithChecked: true,
      approvedAt: new Date(),
      status: "APPROVED",
    },
  });
  const report = await prisma.report.findFirst({ where: { id: reportId, userId: user.id }, include: { case: true } });
  if (!report) throw new Error("Report not found");
  await prisma.submission.create({
    data: {
      caseId: report.caseId,
      reportId,
      providerId: report.providerId,
      status: "APPROVED",
      channel: "human-review",
    },
  });
  await enqueueSubmission(reportId, user.id);
  revalidatePath("/reports");
  revalidatePath("/submissions");
}

export async function saveTemplate(formData: FormData) {
  const user = await requireUser();
  await prisma.reportTemplate.create({
    data: {
      userId: user.id,
      name: String(formData.get("name") ?? "Custom template"),
      reportType: String(formData.get("reportType") ?? "DMCA_COPYRIGHT_NOTICE") as ReportType,
      body: String(formData.get("body") ?? ""),
    },
  });
  revalidatePath("/settings");
}

export async function updateSettings(formData: FormData) {
  const user = await requireUser();
  await prisma.user.update({
    where: { id: user.id },
    data: {
      similarityThreshold: Number(formData.get("similarityThreshold") ?? 70),
    },
  });
  revalidatePath("/settings");
}

export async function markNotificationsRead() {
  const user = await requireUser();
  await prisma.notification.updateMany({ where: { userId: user.id }, data: { read: true } });
  revalidatePath("/activity");
}

export async function bulkUpdateCases(formData: FormData) {
  const user = await requireUser();
  const ids = parseJson<string[]>(String(formData.get("ids") ?? "[]"), []);
  const tag = String(formData.get("tag") ?? "").trim();
  const providerId = String(formData.get("providerId") ?? "") || null;
  const generateDrafts = formData.get("generateDrafts") === "on";
  if (!ids.length) return;
  const cases = await prisma.case.findMany({
    where: { id: { in: ids }, userId: user.id },
    include: { target: true },
  });
  for (const kase of cases) {
    const tags = parseJson<string[]>(kase.tags, []);
    if (tag && !tags.includes(tag)) tags.push(tag);
    await prisma.case.update({
      where: { id: kase.id },
      data: {
        tags: JSON.stringify(tags),
        providerId: providerId ?? kase.providerId,
      },
    });
    if (generateDrafts) {
      await generateReportDraft({
        userId: user.id,
        caseId: kase.id,
        reportType: "DMCA_COPYRIGHT_NOTICE",
      });
    }
  }
  await logActivity({
    userId: user.id,
    userName: "workspace",
    action: "Bulk updated individually identifiable cases",
    result: "SUCCESS",
    metadata: { count: cases.length },
  });
  revalidatePath("/cases");
}

export async function importTargetsCsv(text: string, siteId?: string) {
  const user = await requireUser();
  const lines = text.split(/\r?\n/).slice(1);
  let count = 0;
  for (const line of lines) {
    if (!line.trim()) continue;
    const [domain, url, notes, priority, tags] = line.split(",").map((s) => s?.trim());
    if (!domain) continue;
    await prisma.target.create({
      data: {
        userId: user.id,
        siteId: siteId || null,
        domain: hostnameFromUrl(domain),
        url: normalizeUrl(url || domain),
        notes: notes || null,
        priority: ((priority || "MEDIUM") as TargetPriority) || "MEDIUM",
        tags: JSON.stringify((tags || "").split("|").filter(Boolean)),
      },
    });
    count += 1;
  }
  revalidatePath("/targets");
  return { count };
}
